import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useRescheduleHistoryContext } from '../../../contexts/RescheduleHistoryProvider';
import useRescheduleHistoryActions from '../../../hooks/APIHooks/appointments/useRescheduleHistoryActions';
import { RescheduleHistory } from '../../../types/history';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';
import RescheduleHistoryModal from '../../CommonComps/Modals/RescheduleHistoryModal';

const RescheduleHistoryModalsWrapper = () => {
  // integration of react hooks here
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // integration of context hooks here
  const {
    showDetailsModal,
    setShowDetailsModal,
    showConfirmModal,
    setShowConfirmModal,
    showRejectModal,
    setShowRejectModal,
    rescheduleHistory,
  } = useRescheduleHistoryContext();

  // integration of custom API hooks here
  const { updateRescheduleHistoryStatus } = useRescheduleHistoryActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  const confirmOrRejectAppointmentHandler = async (
    confirmOrReject: 'confirmed' | 'rejected'
  ) => {
    try {
      setIsUpdating(true);

      await updateRescheduleHistoryStatus
        .mutateAsync({
          historyId: rescheduleHistory?.id as number,
          body: {
            appointment_request:
              rescheduleHistory?.appointment_request as number,
            reschedule_status: confirmOrReject,
          },
        })
        .then((res) => {
          if (res.is_success) {
            toast.success(
              `Appointment ${confirmOrReject
                .charAt(0)
                .toUpperCase()
                .concat(confirmOrReject.slice(1))}`
            );

            if (confirmOrReject === 'confirmed') {
              setShowConfirmModal(false);
            } else {
              setShowRejectModal(false);
            }

            queryClient.invalidateQueries({
              queryKey: ['reschedule-history-list'],
            });
          } else {
            toast.error(res.message || 'Status Updating Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsUpdating(false);
    }
  };

  // rendering reschedule modals wrapper section component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDetailsModal ? (
          <RescheduleHistoryModal
            rescheduleHistory={rescheduleHistory as RescheduleHistory}
            handleModalClose={() => setShowDetailsModal(false)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showConfirmModal ? (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to confirm this rescheduled appointment?
              </>
            }
            isLoading={isUpdating}
            yesBtnTxt='Yes'
            yesBtnVariant='success'
            noBtnTxt='No'
            noBtnVariant='danger'
            mutationHandler={() =>
              confirmOrRejectAppointmentHandler('confirmed')
            }
            handleModalClose={() => setShowConfirmModal(false)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showRejectModal ? (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>Are you sure you want to reject this rescheduled appointment?</>
            }
            isLoading={false}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={() =>
              confirmOrRejectAppointmentHandler('rejected')
            }
            handleModalClose={() => setShowRejectModal(false)}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
};

export default RescheduleHistoryModalsWrapper;
